import { brandPalette as palette } from '../../utils/brandPalette';
// src/components/dashboard/AIInsights.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    Avatar,
    Container,
    IconButton,
    Tooltip,
    Skeleton,
} from '@mui/material';
import {
    AutoAwesome as AiIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Analytics as AnalyticsIcon,
    PictureAsPdf as PdfIcon,
    Refresh as RefreshIcon,
    ArrowForward as ArrowForwardIcon,
    Lightbulb as InsightIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Event as EventIcon,
    Handshake as HandshakeIcon,
    Group as GroupIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    Insights as TimelineIcon,
    Assessment as AssessmentIcon,
    Close as CloseIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { useAuth } from '../auth/AuthContext';
import Header from '../common/Header/Header';
import { db } from '../../config/firebase';
import { collection, getDocs, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Register ChartJS
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

// ============================================
// DESIGN TOKENS - Enhanced Glassmorphism
// ============================================



// ============================================
// GLASS STYLES
// ============================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color,
    background: alpha(color, .07),
    border: `1px solid ${alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? '#fff' : color,
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

// ============================================
// GLASS STAT CARD
// ============================================

const GlassStatCard = ({ title, value, icon, color, gradient, delay, change, trend, subtitle, loading }) => {
    const [counted, setCounted] = useState(false);
    const [displayValue, setDisplayValue] = useState(0);
    const nodeRef = useRef(null);

    useEffect(() => {
        if (loading) return;
        const val = typeof value === 'string' && value.includes('%') ? parseInt(value) : value;
        const numValue = typeof val === 'number' ? val : 0;
        
        if (numValue <= 0) {
            setDisplayValue(value);
            return;
        }

        let start = 0;
        const duration = 800;
        const increment = Math.ceil(numValue / 30);
        
        const timer = setInterval(() => {
            start += increment;
            if (start >= numValue) {
                setDisplayValue(value);
                clearInterval(timer);
            } else {
                setDisplayValue(start);
            }
        }, 25);

        return () => clearInterval(timer);
    }, [value, loading]);

    const rgba = (c, opacity) => {
        if (!c) return `rgba(0,0,0,${opacity})`;
        const hex = c.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    ...glassCard({ level: 2, radius: '20px', accent: color }),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transition: `transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        ...glassLift(4, color),
                        '& .stat-glow': { opacity: 0.8, transform: 'scale(1.25)' },
                        '& .stat-pod': { transform: 'scale(1.08) rotate(-4deg)' },
                        '& .stat-sheen': { animation: `tfSheen 0.9s cubic-bezier(0.34,1.56,0.64,1)` },
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '8%',
                        right: '8%',
                        height: '2px',
                        borderRadius: '0 0 999px 999px',
                        background: `linear-gradient(90deg, transparent, ${rgba(color, 0.85)}, transparent)`,
                        pointerEvents: 'none',
                        zIndex: 2,
                    },
                }}
            >
                <Box className="stat-sheen" sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '38%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)',
                    pointerEvents: 'none',
                    zIndex: 2,
                }} />

                <Box className="stat-glow" sx={{
                    position: 'absolute',
                    top: -70,
                    right: -70,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(color, 0.3)}, transparent 70%)`,
                    opacity: 0.35,
                    transition: `all 0.6s cubic-bezier(0.22,1,0.36,1)`,
                    pointerEvents: 'none',
                }} />

                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.05,
                    backgroundImage: `radial-gradient(${color} 0.5px, transparent 0.5px)`,
                    backgroundSize: '16px 16px',
                    maskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                {React.cloneElement(icon, { 
                    sx: { 
                        position: 'absolute', 
                        right: -8, 
                        bottom: -12, 
                        fontSize: 110, 
                        color: color,
                        opacity: 0.07,
                        transform: 'rotate(-12deg) scale(1.2)',
                        pointerEvents: 'none',
                    } 
                })}

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box className="stat-pod" sx={glassPod(color, 44, '14px')}>
                            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
                        </Box>
                        {change && (
                            <Chip
                                label={change}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.55rem',
                                    height: 20,
                                    bgcolor: trend === 'up' ? 'rgba(47,89,66,0.10)' : 'rgba(166,67,47,0.10)',
                                    color: trend === 'up' ? palette.pine : palette.clay,
                                    border: `1px solid ${trend === 'up' ? 'rgba(47,89,66,0.15)' : 'rgba(166,67,47,0.15)'}`,
                                }}
                                icon={trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 11 }} /> : <TrendingDownIcon sx={{ fontSize: 11 }} />}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={36} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 700,
                                fontSize: 'clamp(1.8rem, 3.6vw, 2.4rem)',
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                background: `linear-gradient(145deg, ${palette.ink} 20%, ${color})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                display: 'inline-block',
                                mb: 0.25,
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            color: palette.inkSoft,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontSize: '0.6rem',
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ color: palette.inkLight, mt: 0.5, fontSize: '0.65rem' }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

// ============================================
// GLASS INSIGHT CARD
// ============================================

const GlassInsightCard = ({ insight, index }) => {
    const getIcon = () => {
        switch (insight.type) {
            case 'success': return <CheckCircleIcon sx={{ color: palette.success }} />;
            case 'warning': return <WarningIcon sx={{ color: palette.warning }} />;
            case 'danger': return <CancelIcon sx={{ color: palette.danger }} />;
            case 'info': return <InfoIcon sx={{ color: palette.blue }} />;
            case 'trend_up': return <TrendingUpIcon sx={{ color: palette.pine }} />;
            case 'trend_down': return <TrendingDownIcon sx={{ color: palette.clay }} />;
            default: return <InsightIcon sx={{ color: palette.gold }} />;
        }
    };
    const getBorderColor = () => {
        switch (insight.type) {
            case 'success': return palette.success;
            case 'warning': return palette.warning;
            case 'danger': return palette.danger;
            case 'info': return palette.blue;
            case 'trend_up': return palette.pine;
            case 'trend_down': return palette.clay;
            default: return palette.gold;
        }
    };
    const getBgColor = () => `${getBorderColor()}08`;
    const getHoverBg = () => `${getBorderColor()}12`;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    ...glassCard({ level: 1, radius: '16px', accent: getBorderColor() }),
                    p: 2.5,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: `transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        ...glassLift(3, getBorderColor()),
                    },
                }}
            >
                <Box sx={{ alignItems: 'flex-start', gap: 2, display: 'flex' }}>
                    <Box sx={{ mt: 0.5 }}>{getIcon()}</Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.95rem' }}>
                            {insight.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, fontSize: '0.85rem', lineHeight: 1.6 }}>
                            {insight.description}
                        </Typography>
                        {insight.action && (
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                                onClick={insight.action}
                                sx={{
                                    mt: 1,
                                    color: getBorderColor(),
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    ...glassPill(getBorderColor()),
                                    '&:hover': { background: getHoverBg() },
                                }}
                            >
                                {insight.actionLabel || 'Take Action'}
                            </Button>
                        )}
                    </Box>
                    <Chip
                        label={insight.category || 'Insight'}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.5rem',
                            fontWeight: 600,
                            bgcolor: `${getBorderColor()}12`,
                            color: getBorderColor(),
                            borderRadius: '4px',
                            border: `1px solid ${getBorderColor()}20`,
                        }}
                    />
                </Box>
            </Paper>
        </motion.div>
    );
};

// ============================================
// FLOATING ORBS - Enhanced
// ============================================

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 520, top: '-10%', left: '4%', duration: 28, delay: 0 },
        { color: palette.pine, size: 440, top: '15%', left: '78%', duration: 34, delay: 2 },
        { color: palette.gold, size: 380, top: '55%', left: '-8%', duration: 26, delay: 1 },
        { color: palette.purple, size: 400, top: '70%', left: '68%', duration: 32, delay: 3 },
        { color: palette.teal, size: 320, top: '35%', left: '44%', duration: 30, delay: 1.5 },
        { color: palette.rust, size: 280, top: '8%', left: '45%', duration: 36, delay: 4 },
    ];

    const rgba = (color, opacity) => {
        if (!color) return `rgba(0,0,0,${opacity})`;
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };

    return (
        <Box
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
            }}
        >
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{
                        opacity: [0.4, 0.7, 0.4],
                        x: [0, 50, -40, 0],
                        y: [0, -45, 35, 0],
                        scale: [1, 1.15, 0.9, 1],
                    }}
                    transition={{
                        duration: orb.duration,
                        delay: orb.delay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${rgba(orb.color, 0.15)}, ${rgba(orb.color, 0.02)} 70%, transparent 100%)`,
                        filter: 'blur(12px)',
                        willChange: 'transform, opacity',
                    }}
                />
            ))}

            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    linear-gradient(${rgba(palette.ink, 0.015)} 1px, transparent 1px),
                    linear-gradient(90deg, ${rgba(palette.ink, 0.015)} 1px, transparent 1px)
                `,
                backgroundSize: '64px 64px',
                opacity: 0.4,
            }} />
        </Box>
    );
};

// ============================================
// SECTION RULE
// ============================================

const SectionRule = ({ color = palette.rust, width = 44 }) => (
    <Box
        sx={{
            width,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${color}, ${alpha(color, 0)})`,
            mt: 0.75,
            animation: 'shimmerRule 3.2s ease-in-out infinite',
        }}
    />
);

// ============================================
// MAIN COMPONENT
// ============================================

const AIInsights = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [insights, setInsights] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [stats, setStats] = useState({
        totalMeetings: 0, completedMeetings: 0, pendingMeetings: 0,
        totalInvitations: 0, acceptedInvitations: 0, pendingInvitations: 0,
        totalConnections: 0, engagementRate: 0, connectionScore: 0,
        teamMembers: 0,
    });
    const [monthlyData, setMonthlyData] = useState({ meetings: Array(12).fill(0), connections: Array(12).fill(0), invitations: Array(12).fill(0) });
    const [loadingInsights, setLoadingInsights] = useState(false);

    const getCompanyId = () => {
        return companyData?.companyId || userData?.companyId || companyData?.id || userData?.id || currentUser?.uid;
    };

    const getCompanyName = () => {
        return companyData?.companyName || userData?.companyName || companyData?.name || 'Your Company';
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const companyId = getCompanyId();
            
            if (!companyId) {
                console.warn('No company ID found');
                setLoading(false);
                return;
            }

            const companyRef = doc(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);
            if (companyDoc.exists()) {
                setCompanyInfo({ id: companyDoc.id, ...companyDoc.data() });
            }

            const meetingsRef = collection(db, 'meetings');
            const meetingsQuery = query(meetingsRef, where('companyId', '==', companyId));
            const meetingsSnapshot = await getDocs(meetingsQuery);
            const meetingsList = [];
            let totalMeetings = 0, completedMeetings = 0, pendingMeetings = 0;
            meetingsSnapshot.forEach((doc) => {
                const data = doc.data();
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    totalMeetings++;
                    meetingsList.push({ id: doc.id, ...data });
                    if (data.status === 'completed') completedMeetings++;
                    if (data.status === 'pending' || data.status === 'scheduled') pendingMeetings++;
                }
            });

            const invitationsRef = collection(db, 'invitations');
            const invitationsQuery = query(invitationsRef, where('companyId', '==', companyId));
            const invitationsSnapshot = await getDocs(invitationsQuery);
            const invitationsList = [];
            let totalInvitations = 0, acceptedInvitations = 0, pendingInvitations = 0;
            invitationsSnapshot.forEach((doc) => {
                const data = doc.data();
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    totalInvitations++;
                    invitationsList.push({ id: doc.id, ...data });
                    if (data.status === 'accepted') acceptedInvitations++;
                    if (data.status === 'pending') pendingInvitations++;
                }
            });

            const usersRef = collection(db, 'users');
            const usersQuery = query(usersRef, where('companyId', '==', companyId));
            const usersSnapshot = await getDocs(usersQuery);
            let teamMembers = 0;
            usersSnapshot.forEach((doc) => {
                if (doc.id !== '_schema' && doc.id !== '_placeholder') {
                    teamMembers++;
                }
            });

            const totalConnections = acceptedInvitations + completedMeetings;
            const engagementRate = totalMeetings > 0 ? Math.round((completedMeetings / totalMeetings) * 100) : 0;
            const connectionScore = totalInvitations > 0 ? Math.round((acceptedInvitations / totalInvitations) * 100) : 0;

            setStats({
                totalMeetings: totalMeetings,
                completedMeetings: completedMeetings,
                pendingMeetings: pendingMeetings,
                totalInvitations: totalInvitations,
                acceptedInvitations: acceptedInvitations,
                pendingInvitations: pendingInvitations,
                totalConnections: totalConnections,
                engagementRate: engagementRate,
                connectionScore: connectionScore,
                teamMembers: teamMembers,
            });

            const meetingCounts = Array(12).fill(0);
            const connectionCounts = Array(12).fill(0);
            const invitationCounts = Array(12).fill(0);

            meetingsList.forEach(meeting => {
                if (meeting.createdAt) {
                    const date = meeting.createdAt.toDate ? meeting.createdAt.toDate() : new Date(meeting.createdAt);
                    const month = date.getMonth();
                    meetingCounts[month]++;
                    if (meeting.status === 'completed' || meeting.status === 'confirmed') connectionCounts[month]++;
                }
            });

            invitationsList.forEach(invitation => {
                if (invitation.createdAt) {
                    const date = invitation.createdAt.toDate ? invitation.createdAt.toDate() : new Date(invitation.createdAt);
                    const month = date.getMonth();
                    invitationCounts[month]++;
                }
            });

            setMonthlyData({ meetings: meetingCounts, connections: connectionCounts, invitations: invitationCounts });

            generateInsights(meetingsList, invitationsList, {
                totalMeetings: totalMeetings,
                completedMeetings: completedMeetings,
                pendingMeetings: pendingMeetings,
                totalInvitations: totalInvitations,
                acceptedInvitations: acceptedInvitations,
                pendingInvitations: pendingInvitations,
                totalConnections: totalConnections,
                engagementRate: engagementRate,
                connectionScore: connectionScore,
                teamMembers: teamMembers,
            });

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load insights data');
        } finally {
            setLoading(false);
        }
    };

    const generateInsights = (meetingsData, invitationsData, statsData) => {
        setLoadingInsights(true);
        setTimeout(() => {
            const newInsights = [];
            
            if (statsData.engagementRate > 70) {
                newInsights.push({ 
                    type: 'success', 
                    title: '🌟 High Engagement Rate', 
                    description: `Your engagement rate of ${statsData.engagementRate}% is excellent! This indicates strong interest from potential partners. Consider leveraging this momentum to schedule more meetings.`, 
                    category: 'Engagement', 
                    action: () => navigate('/dashboard/meetings/schedule'), 
                    actionLabel: 'Schedule More Meetings' 
                });
            } else if (statsData.engagementRate > 40) {
                newInsights.push({ 
                    type: 'info', 
                    title: '📊 Moderate Engagement', 
                    description: `Your engagement rate is ${statsData.engagementRate}%. There's room for improvement. Consider reaching out to more companies or enhancing your profile to attract more interest.`, 
                    category: 'Engagement', 
                    action: () => navigate('/dashboard/company'), 
                    actionLabel: 'Update Profile' 
                });
            } else {
                newInsights.push({ 
                    type: 'warning', 
                    title: '⚠️ Low Engagement Detected', 
                    description: `Your engagement rate is only ${statsData.engagementRate}%. This is below average. We recommend actively reaching out to more companies and participating in more networking events.`, 
                    category: 'Engagement', 
                    action: () => navigate('/dashboard/companies'), 
                    actionLabel: 'Find Companies' 
                });
            }

            if (statsData.connectionScore > 70) {
                newInsights.push({ 
                    type: 'trend_up', 
                    title: '💪 Strong Connection Score', 
                    description: `Your connection score of ${statsData.connectionScore}% is excellent. You're building strong relationships with ${statsData.totalConnections} connections.`, 
                    category: 'Connections' 
                });
            } else if (statsData.connectionScore > 40) {
                newInsights.push({ 
                    type: 'info', 
                    title: '📈 Building Connections', 
                    description: `Your connection score is ${statsData.connectionScore}%. You have ${statsData.totalConnections} connections. Keep engaging to strengthen your network.`, 
                    category: 'Connections' 
                });
            } else {
                newInsights.push({ 
                    type: 'trend_down', 
                    title: '🤝 Focus on Connections', 
                    description: `Your connection score is ${statsData.connectionScore}%. You have ${statsData.totalConnections} connections. Consider sending more invitations and following up.`, 
                    category: 'Connections', 
                    action: () => navigate('/dashboard/companies'), 
                    actionLabel: 'Connect with Companies' 
                });
            }

            if (statsData.teamMembers > 0) {
                newInsights.push({ 
                    type: 'info', 
                    title: '👥 Team Collaboration', 
                    description: `Your company has ${statsData.teamMembers} team member(s). ${statsData.teamMembers > 1 ? 'Collaborate with your team to expand your network and manage opportunities.' : 'Consider adding more team members to expand your reach.'}`, 
                    category: 'Team', 
                    action: () => navigate('/dashboard/company/team'), 
                    actionLabel: statsData.teamMembers > 1 ? 'Manage Team' : 'Add Team Members' 
                });
            }

            if (statsData.pendingInvitations > 0) {
                newInsights.push({ 
                    type: 'warning', 
                    title: '📬 Pending Invitations', 
                    description: `You have ${statsData.pendingInvitations} pending invitation(s). Follow up with these companies to increase your connection rate.`, 
                    category: 'Invitations', 
                    action: () => navigate('/dashboard/companies'), 
                    actionLabel: 'View Pending' 
                });
            }

            const recommendations = [];
            if (statsData.engagementRate < 50) {
                recommendations.push('improving engagement by updating your company profile and reaching out to more companies');
            } else if (statsData.connectionScore < 50) {
                recommendations.push('following up on invitations and scheduling more meetings');
            } else {
                recommendations.push('expanding your network by connecting with companies in new regions or services');
            }

            newInsights.push({ 
                type: 'info', 
                title: '🤖 AI-Powered Recommendation', 
                description: `Based on your activity data, we recommend ${recommendations[0]}. This strategy will help accelerate your growth and increase your business opportunities.`, 
                category: 'AI Suggestion', 
                action: () => navigate('/dashboard/company'), 
                actionLabel: 'View Recommendations' 
            });

            setInsights(newInsights);
            setLoadingInsights(false);
        }, 1000);
    };

    const generatePDFReport = async () => {
        setGeneratingReport(true);
        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let y = 20;

            doc.setFillColor(245, 242, 234);
            doc.rect(0, 0, pageWidth, 50, 'F');
            doc.setFont('helvetica', 'bold'); doc.setFontSize(24); doc.setTextColor(33, 26, 20); doc.text('TELCOFAIR', 20, 30);
            doc.setFont('helvetica', 'normal'); doc.setFontSize(12); doc.setTextColor(107, 93, 79); doc.text('AI Insights Report', 20, 40);
            
            const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            doc.setFontSize(10); doc.text(`Generated: ${today}`, pageWidth - 50, 30);
            doc.setFontSize(12); doc.setTextColor(33, 26, 20); doc.text(`Company: ${getCompanyName()}`, pageWidth - 50, 40);

            y = 60;
            doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.text('Key Metrics', 20, y); y += 10;

            const reportStatsData = [
                ['Total Meetings', stats.totalMeetings], ['Completed Meetings', stats.completedMeetings],
                ['Pending Meetings', stats.pendingMeetings], ['Total Invitations', stats.totalInvitations],
                ['Accepted Invitations', stats.acceptedInvitations], ['Total Connections', stats.totalConnections],
                ['Engagement Rate', `${stats.engagementRate}%`], ['Connection Score', `${stats.connectionScore}%`],
                ['Team Members', stats.teamMembers],
            ];

            doc.autoTable({
                startY: y, head: [['Metric', 'Value']], body: reportStatsData, theme: 'grid',
                headStyles: { fillColor: [47, 89, 66], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [247, 242, 234] }, margin: { left: 20, right: 20 }, styles: { fontSize: 10 },
            });

            y = doc.lastAutoTable.finalY + 15;
            doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(33, 26, 20); doc.text('AI-Generated Insights', 20, y); y += 10;

            insights.forEach((insight, index) => {
                const splitText = doc.splitTextToSize(`${index + 1}. ${insight.title}: ${insight.description}`, pageWidth - 40);
                const textHeight = splitText.length * 5;
                if (y + textHeight + 20 > pageHeight - 20) { doc.addPage(); y = 20; }
                
                doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(33, 26, 20); doc.text(`${index + 1}. ${insight.title}`, 20, y); y += 6;
                doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(107, 93, 79); doc.text(splitText, 25, y); y += textHeight + 8;
            });

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i); doc.setFontSize(8); doc.setTextColor(107, 93, 79);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 10);
                doc.text(`Generated by TELCOFAIR AI Insights - ${getCompanyName()}`, 20, pageHeight - 10);
            }

            doc.save(`AI_Insights_Report_${getCompanyName().replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('Report downloaded successfully!');
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Failed to generate report');
        } finally {
            setGeneratingReport(false);
        }
    };

    // ============================================
    // CHART CONFIG
    // ============================================
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 15,
                    font: { family: "'Plus Jakarta Sans', sans-serif", size: 11, weight: 600 },
                    color: palette.inkSoft,
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(33,26,20,0.05)', drawBorder: false },
                ticks: { font: { family: "'Plus Jakarta Sans', sans-serif", size: 10 }, color: palette.inkLight, stepSize: 1 }
            },
            x: {
                grid: { display: false },
                ticks: { font: { family: "'Plus Jakarta Sans', sans-serif", size: 10 }, color: palette.inkLight }
            },
        },
    };
    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 12,
                    font: { family: "'Plus Jakarta Sans', sans-serif", size: 11, weight: 600 },
                    color: palette.inkSoft,
                }
            }
        }
    };
    const doughnutOptions = { ...pieOptions, cutout: '60%' };

    useEffect(() => {
        fetchData();
        const companyId = getCompanyId();
        if (companyId) {
            const unsubscribeMeetings = onSnapshot(
                query(collection(db, 'meetings'), where('companyId', '==', companyId)), 
                () => fetchData()
            );
            const unsubscribeInvitations = onSnapshot(
                query(collection(db, 'invitations'), where('companyId', '==', companyId)), 
                () => fetchData()
            );
            return () => { unsubscribeMeetings(); unsubscribeInvitations(); };
        }
    }, []);

    // ============================================
    // RENDER
    // ============================================

    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                position: 'relative',
                bgcolor: palette.canvas,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
                
                <Header />
                <Box sx={{
                    width: '100%',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '60vh',
                    zIndex: 1,
                    position: 'relative',
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Box sx={glassPod(palette.purple, 72, '50%')}>
                            <AiIcon sx={{ fontSize: 32, color: '#fff', animation: 'pulse 2s infinite' }} />
                        </Box>
                        <Typography sx={{ color: palette.inkSoft, mt: 2, fontSize: '1.1rem', fontWeight: 600 }}>
                            Loading AI Insights for {getCompanyName()}...
                        </Typography>
                        <Box sx={{ mt: 2, maxWidth: 300, mx: 'auto' }}>
                            <LinearProgress sx={{ height: 4, borderRadius: 4, bgcolor: palette.hairline, '& .MuiLinearProgress-bar': { bgcolor: palette.purple } }} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    const statsDataArray = [
        { 
            title: 'Total Meetings', 
            value: stats.totalMeetings, 
            icon: <EventIcon />, 
            color: palette.rust, 
            gradient: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
            change: stats.totalMeetings > 0 ? '+12%' : '0%', 
            trend: 'up', 
            subtitle: `${stats.completedMeetings} completed` 
        },
        { 
            title: 'Connections', 
            value: stats.totalConnections, 
            icon: <HandshakeIcon />, 
            color: palette.pine, 
            gradient: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
            change: stats.totalConnections > 0 ? '+8%' : '0%', 
            trend: 'up', 
            subtitle: `${stats.acceptedInvitations} from invitations` 
        },
        { 
            title: 'Engagement Rate', 
            value: `${stats.engagementRate}%`, 
            icon: <AssessmentIcon />, 
            color: palette.teal, 
            gradient: `linear-gradient(135deg, ${palette.teal}, #2a8a93)`,
            change: stats.engagementRate > 50 ? '+15%' : '-5%', 
            trend: stats.engagementRate > 50 ? 'up' : 'down', 
            subtitle: stats.engagementRate > 50 ? 'Excellent' : 'Needs improvement' 
        },
        { 
            title: 'Connection Score', 
            value: `${stats.connectionScore}%`, 
            icon: <StarsIcon />, 
            color: palette.purple, 
            gradient: `linear-gradient(135deg, ${palette.purple}, ${palette.navy})`,
            change: stats.connectionScore > 50 ? '+10%' : '-3%', 
            trend: stats.connectionScore > 50 ? 'up' : 'down', 
            subtitle: `${stats.totalInvitations} total invitations` 
        }
    ];

    return (
        <Box className="erp-page-shell erp-internal-page tf-ai-insights-page" sx={{
            minHeight: '100vh',
            position: 'relative',
            bgcolor: palette.canvas,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            overflow: 'hidden',
        }}>
            <style>
                {`
                    @keyframes tfPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
                    @keyframes tfFloat { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-10px, -10px) rotate(5deg); } }
                    @keyframes tfAurora { 0% { background-position: 0% 0%; } 50% { background-position: 100% 60%; } 100% { background-position: 0% 0%; } }
                    @keyframes tfGrain { 0% { transform: translate(0, 0); } 20% { transform: translate(-2%, 2%); } 40% { transform: translate(2%, -1%); } 60% { transform: translate(-1%, -2%); } 80% { transform: translate(1%, 1%); } 100% { transform: translate(0, 0); } }
                    @keyframes shimmerRule { 0%, 100% { opacity: 0.7; width: 40px; } 50% { opacity: 1; width: 56px; } }
                    @keyframes tfSheen { 0% { transform: translateX(-130%) skewX(-18deg); } 100% { transform: translateX(320%) skewX(-18deg); } }
                `}
            </style>

            
            <Box sx={{ position: 'relative', zIndex: 2 }}><Header /></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 1.25, sm: 2, lg: 2.5 },
                    py: { xs: 1.1, md: 1.35 },
                    width: '100%',
                    maxWidth: '1780px',
                    mx: 'auto',
                }}
            >
                {/* Header Section */}
                <Box>
                    <Box
                        className="erp-page-hero tf-dashboard-reference-hero"
                        sx={{
                            ...glassCard({ level: 2, radius: '18px', accent: palette.purple }),
                            p: { xs: 1.8, md: 2.2 },
                            mb: 1.45,
                            overflow: 'hidden',
                            position: 'relative',
                            minHeight: { xs: 118, md: 132 },
                            color: '#FFFFFF',
                            background: `linear-gradient(118deg, ${palette.purple} 0%, ${palette.purple} 48%, ${palette.teal} 100%)`,
                            border: '1px solid rgba(255,255,255,.22)',
                            boxShadow: `0 18px 44px ${alpha(palette.purple, .16)}, 0 4px 12px rgba(25,57,78,.08)`,
                        }}
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: '6%',
                            right: '6%',
                            height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${palette.purple}, ${palette.gold}, ${palette.pine}, transparent)`,
                            backgroundSize: '300% 100%',
                            animation: 'tfAurora 11s linear infinite',
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{
                            position: 'absolute',
                            top: -120,
                            right: -120,
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${palette.purple}20, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{
                            position: 'absolute',
                            bottom: -80,
                            left: -80,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${palette.gold}15, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, position: 'relative', zIndex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={glassPod(palette.purple, 56, '16px')}>
                                    <AiIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: { xs: '1.45rem', md: '1.9rem' },
                                        color: '#FFFFFF',
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.15,
                                    }}>
                                        AI Insights
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,.86)', fontSize: '0.78rem', mt: 0.25 }}>
                                        Smart analytics and recommendations for <strong>{getCompanyName()}</strong>
                                    </Typography>
                                    <SectionRule color={palette.purple} width={60} />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                                    label={getCompanyName()}
                                    sx={{
                                        background: 'rgba(255,255,255,.13)',
                                        color: '#FFFFFF',
                                        border: '1px solid rgba(255,255,255,.24)',
                                        px: 1.2,
                                        height: 32,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon sx={{ animation: loading ? 'tfPulse 1s linear infinite' : 'none' }} />}
                                    onClick={fetchData}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(255,255,255,.30)',
                                        background: 'rgba(255,255,255,.10)',
                                        '&:hover': { background: 'rgba(255,255,255,.18)', borderColor: 'rgba(255,255,255,.46)', color: '#FFFFFF' }
                                    }}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<PdfIcon />}
                                    onClick={generatePDFReport}
                                    disabled={generatingReport}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.8rem',
                                        py: 0.8,
                                        px: 2.5,
                                        background: `linear-gradient(135deg, ${palette.purple}, ${palette.navy})`,
                                        boxShadow: '0 4px 16px rgba(106,27,154,0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(106,27,154,0.4)',
                                            transform: 'translateY(-2px)',
                                        }
                                    }}
                                >
                                    {generatingReport ? 'Generating...' : 'Export Report'}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* STAT CARDS */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.35,
                    mb: 1.7,
                    width: '100%',
                }}>
                    {statsDataArray.map((stat, index) => (
                        <GlassStatCard key={stat.title} {...stat} delay={index * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* CHARTS ROW 1 */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
                    gap: 1.35,
                    mb: 1.7,
                    width: '100%',
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '22px', accent: palette.rust }),
                            p: { xs: 1.7, md: 1.9 },
                            minHeight: 300,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                    Activity Trends
                                </Typography>
                                <SectionRule color={palette.rust} />
                            </Box>
                            <Chip
                                label="Last 12 Months"
                                size="small"
                                sx={{ ...glassPill(palette.rust), fontSize: '0.6rem' }}
                            />
                        </Box>
                        <Box sx={{ flexGrow: 1, minHeight: 220, position: 'relative' }}>
                            <Line 
                                data={{
                                    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                                    datasets: [
                                        {
                                            label: 'Meetings',
                                            data: monthlyData.meetings,
                                            borderColor: palette.rust,
                                            backgroundColor: `${palette.rust}15`,
                                            fill: true,
                                            tension: 0.4,
                                            pointBackgroundColor: palette.rust,
                                            pointBorderColor: '#fff',
                                            pointBorderWidth: 2,
                                            pointRadius: 4,
                                            borderWidth: 2.5,
                                        },
                                        {
                                            label: 'Connections',
                                            data: monthlyData.connections,
                                            borderColor: palette.pine,
                                            backgroundColor: `${palette.pine}15`,
                                            fill: true,
                                            tension: 0.4,
                                            pointBackgroundColor: palette.pine,
                                            pointBorderColor: '#fff',
                                            pointBorderWidth: 2,
                                            pointRadius: 4,
                                            borderWidth: 2.5,
                                        },
                                        {
                                            label: 'Invitations',
                                            data: monthlyData.invitations,
                                            borderColor: palette.gold,
                                            backgroundColor: `${palette.gold}15`,
                                            fill: true,
                                            tension: 0.4,
                                            pointBackgroundColor: palette.gold,
                                            pointBorderColor: '#fff',
                                            pointBorderWidth: 2,
                                            pointRadius: 4,
                                            borderWidth: 2.5,
                                        }
                                    ]
                                }}
                                options={chartOptions}
                            />
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '22px', accent: palette.pine }),
                            p: { xs: 1.7, md: 1.9 },
                            minHeight: 300,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                    Meeting Status
                                </Typography>
                                <SectionRule color={palette.pine} />
                            </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, minHeight: 220, position: 'relative' }}>
                            <Doughnut 
                                data={{
                                    labels: ['Completed Meetings', 'Pending Meetings'],
                                    datasets: [
                                        { 
                                            data: [stats.completedMeetings || 1, stats.pendingMeetings || 1],
                                            backgroundColor: [palette.pine, palette.gold],
                                            borderColor: '#fff',
                                            borderWidth: 2,
                                        }
                                    ]
                                }}
                                options={doughnutOptions}
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* CHARTS ROW 2 */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 1.35,
                    mb: 1.7,
                    width: '100%',
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '22px', accent: palette.teal }),
                            p: { xs: 1.7, md: 1.9 },
                            minHeight: 280,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                    Monthly Performance
                                </Typography>
                                <SectionRule color={palette.teal} />
                            </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, minHeight: 240, position: 'relative' }}>
                            <Bar 
                                data={{
                                    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                                    datasets: [
                                        { 
                                            label: 'Monthly Activity', 
                                            data: monthlyData.meetings.map((v, i) => v + monthlyData.connections[i] + monthlyData.invitations[i]),
                                            backgroundColor: monthlyData.meetings.map((_, i) => {
                                                const colors = [palette.rust, palette.pine, palette.teal, palette.gold, palette.purple];
                                                return `${colors[i % 5]}70`;
                                            }),
                                            borderRadius: 6,
                                        }
                                    ]
                                }}
                                options={chartOptions}
                            />
                        </Box>
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '22px', accent: palette.gold }),
                            p: { xs: 1.7, md: 1.9 },
                            minHeight: 280,
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                    Invitation Distribution
                                </Typography>
                                <SectionRule color={palette.gold} />
                            </Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, minHeight: 240, position: 'relative' }}>
                            <Pie 
                                data={{
                                    labels: ['Accepted Invitations', 'Pending Invitations'],
                                    datasets: [
                                        { 
                                            data: [stats.acceptedInvitations || 1, stats.pendingInvitations || 1],
                                            backgroundColor: [palette.pine, palette.gold],
                                            borderColor: '#fff',
                                            borderWidth: 2,
                                        }
                                    ]
                                }}
                                options={pieOptions}
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* AI INSIGHTS SECTION */}
                <Box sx={{ mb: 1.7, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.35 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={glassPod(palette.purple, 40, '11px')}>
                                <InsightIcon sx={{ fontSize: 19 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                    AI-Generated Insights
                                </Typography>
                                <SectionRule color={palette.purple} />
                            </Box>
                            <Chip
                                label={`${insights.length} insights`}
                                size="small"
                                sx={{ ...glassPill(palette.purple, true), fontSize: '0.6rem', height: 22 }}
                            />
                        </Box>
                        <Button
                            size="small"
                            startIcon={<RefreshIcon sx={{ animation: loadingInsights ? 'tfPulse 1s linear infinite' : 'none' }} />}
                            onClick={() => generateInsights([], [], stats)}
                            disabled={loadingInsights}
                            sx={{
                                color: palette.inkSoft,
                                fontWeight: 600,
                                textTransform: 'none',
                                ...glassPill(palette.inkSoft),
                                '&:hover': { color: palette.rust, background: `${palette.rust}12` }
                            }}
                        >
                            {loadingInsights ? 'Analyzing...' : 'Refresh Insights'}
                        </Button>
                    </Box>

                    {loadingInsights ? (
                        <Paper
                            elevation={0}
                            sx={{
                                ...glassCard({ level: 2, radius: '20px', accent: palette.purple }),
                                p: 4,
                                textAlign: 'center',
                            }}
                        >
                            <Box sx={glassPod(palette.purple, 64, '50%')}>
                                <AiIcon sx={{ fontSize: 28, color: '#fff', animation: 'tfPulse 2s infinite' }} />
                            </Box>
                            <Typography sx={{ color: palette.inkSoft, mt: 2, fontSize: '1rem', fontWeight: 600 }}>
                                AI is analyzing your business data...
                            </Typography>
                            <Box sx={{ mt: 2, maxWidth: 300, mx: 'auto' }}>
                                <LinearProgress sx={{ height: 4, borderRadius: 4, bgcolor: palette.hairline, '& .MuiLinearProgress-bar': { bgcolor: palette.purple } }} />
                            </Box>
                        </Paper>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 3,
                            width: '100%',
                        }}>
                            {insights.map((insight, index) => (
                                <GlassInsightCard key={index} insight={insight} index={index} />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* DETAILED STATS TABLE */}
                <Box sx={{ width: '100%' }}>
                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '22px', accent: palette.gold }),
                            p: { xs: 2.5, md: 3 },
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                            <Box>
                                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: palette.ink }}>
                                    Detailed Analytics
                                </Typography>
                                <SectionRule color={palette.gold} />
                            </Box>
                            <Chip
                                label={`${Object.keys(stats).length} metrics`}
                                size="small"
                                sx={{ ...glassPill(palette.gold), fontSize: '0.6rem' }}
                            />
                        </Box>

                        <TableContainer>
                            <Table sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: `${palette.canvas}50` }}>
                                        <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Metric
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">
                                            Value
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">
                                            Trend
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        { metric: 'Total Meetings', value: stats.totalMeetings, trend: stats.totalMeetings > 0 ? '+12%' : '0%', status: stats.totalMeetings > 0 ? 'Active' : 'No meetings yet' },
                                        { metric: 'Completed Meetings', value: stats.completedMeetings, trend: stats.completedMeetings > 0 ? '+10%' : '0%', status: stats.completedMeetings > 0 ? 'Good' : 'Pending' },
                                        { metric: 'Pending Meetings', value: stats.pendingMeetings, trend: stats.pendingMeetings > 0 ? '-5%' : '0%', status: stats.pendingMeetings > 0 ? 'Action needed' : 'All clear' },
                                        { metric: 'Total Invitations', value: stats.totalInvitations, trend: stats.totalInvitations > 0 ? '+20%' : '0%', status: stats.totalInvitations > 0 ? 'Active outreach' : 'Start inviting' },
                                        { metric: 'Accepted Invitations', value: stats.acceptedInvitations, trend: stats.acceptedInvitations > 0 ? '+18%' : '0%', status: stats.acceptedInvitations > 0 ? 'Positive' : 'Pending' },
                                        { metric: 'Total Connections', value: stats.totalConnections, trend: stats.totalConnections > 0 ? '+15%' : '0%', status: stats.totalConnections > 0 ? 'Growing' : 'Build connections' },
                                        { metric: 'Engagement Rate', value: `${stats.engagementRate}%`, trend: stats.engagementRate > 50 ? '+15%' : '-5%', status: stats.engagementRate > 50 ? 'Excellent' : 'Needs Improvement' },
                                        { metric: 'Connection Score', value: `${stats.connectionScore}%`, trend: stats.connectionScore > 50 ? '+10%' : '-2%', status: stats.connectionScore > 50 ? 'Strong' : 'Building' },
                                        { metric: 'Team Members', value: stats.teamMembers, trend: stats.teamMembers > 0 ? '+5%' : '0%', status: stats.teamMembers > 0 ? 'Team active' : 'Add members' },
                                    ].map((row, index) => {
                                        const isPositive = ['Active', 'Good', 'All clear', 'Positive', 'Growing', 'Excellent', 'Strong', 'Team active'].includes(row.status);
                                        const isWarning = ['Needs Improvement', 'Building', 'Pending', 'Action needed'].includes(row.status);
                                        const isNeutral = ['No meetings yet', 'Start inviting', 'Build connections', 'Add members'].includes(row.status);

                                        return (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    '&:hover': { bgcolor: `${palette.canvas}50` },
                                                    borderBottom: `1px solid ${palette.hairline}`,
                                                    transition: 'background-color 0.2s ease',
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.85rem' }}>
                                                    {row.metric}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, color: palette.ink, fontSize: '1rem' }}>
                                                    {row.value}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip 
                                                        label={row.trend} 
                                                        size="small" 
                                                        sx={{ 
                                                            height: 20,
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            ...glassPill(
                                                                row.trend.startsWith('+') ? palette.pine : 
                                                                row.trend === '0%' ? palette.inkLight : 
                                                                palette.clay
                                                            ),
                                                        }} 
                                                        icon={row.trend.startsWith('+') ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : row.trend === '0%' ? null : <TrendingDownIcon sx={{ fontSize: 12 }} />} 
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip 
                                                        label={row.status} 
                                                        size="small" 
                                                        sx={{ 
                                                            height: 20,
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            ...glassPill(
                                                                isPositive ? palette.success : 
                                                                isWarning ? palette.warning : 
                                                                palette.inkLight
                                                            ),
                                                            color: isPositive ? palette.success : isWarning ? palette.warning : palette.inkLight,
                                                        }} 
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </Container>

            <style>
                {`
                    @keyframes tfPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
                    @keyframes tfFloat { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-10px, -10px) rotate(5deg); } }
                    @keyframes tfAurora { 0% { background-position: 0% 0%; } 50% { background-position: 100% 60%; } 100% { background-position: 0% 0%; } }
                    @keyframes tfGrain { 0% { transform: translate(0, 0); } 20% { transform: translate(-2%, 2%); } 40% { transform: translate(2%, -1%); } 60% { transform: translate(-1%, -2%); } 80% { transform: translate(1%, 1%); } 100% { transform: translate(0, 0); } }
                    @keyframes shimmerRule { 0%, 100% { opacity: 0.7; width: 40px; } 50% { opacity: 1; width: 56px; } }
                    @keyframes tfSheen { 0% { transform: translateX(-130%) skewX(-18deg); } 100% { transform: translateX(320%) skewX(-18deg); } }
                `}
            </style>
        </Box>
    );
};

export default AIInsights;